import React, { useEffect, useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useGetApprovalLimitGroupsQuery } from '../../../../../api/endpoints/approvalLimitGroups';
import { useGetApprovalLimitByIdQuery } from '../../../../../api/endpoints/approvalLimits';
import { formatDateOnly } from '../../../../../utils/dateUtils';

const defaultFormState = {
    id: null,
    startDate: null,
    finishDate: null,
    startingPrice: null,
    endingPrice: null,
    currency: 'TRY',
    approvalGroupId: null
};

const ApprovalLimitsForm = ({ visible, onHide, onSubmit, editMode = false, selectedItem = null }) => {
    const { th } = useLanguage();
    const [formData, setFormData] = useState(defaultFormState);
    const [localSubmitted, setLocalSubmitted] = useState(false);
    const toast = useRef(null);

    // Get approval groups for dropdown
    const { data: approvalGroupsData } = useGetApprovalLimitGroupsQuery({
        IsActive: 1
    });

    // Get approval limit details for edit mode
    const { data: limitDetails, isLoading: isLoadingDetails } = useGetApprovalLimitByIdQuery(selectedItem?.id, {
        skip: !editMode || !selectedItem?.id || !visible
    });

    // Currency options
    const currencyOptions = [
        { label: 'TRY', value: 'TRY' },
        { label: 'USD', value: 'USD' },
        { label: 'EUR', value: 'EUR' }
    ];

    useEffect(() => {
        if (!visible) {
            setFormData(defaultFormState);
            setLocalSubmitted(false);
        }
    }, [visible]);

    useEffect(() => {
        if (visible) {
            if (editMode && limitDetails) {
                // Edit mode için API'den gelen veriyi form'a aktar
                setFormData({
                    id: limitDetails.id,
                    startDate: limitDetails.startDate ? new Date(limitDetails.startDate) : null,
                    finishDate: limitDetails.finishDate ? new Date(limitDetails.finishDate) : null,
                    startingPrice: limitDetails.startingPrice ?? null,
                    endingPrice: limitDetails.endingPrice ?? null,
                    currency: limitDetails.currency ?? 'TRY',
                    approvalGroupId: limitDetails.approvalGroupId ?? null
                });
            } else if (editMode && selectedItem) {
                // Fallback: selectedItem'dan veri aktar (API henüz yüklenmedi)
                setFormData({
                    id: selectedItem.id,
                    startDate: selectedItem.startDate ? new Date(selectedItem.startDate) : null,
                    finishDate: selectedItem.finishDate ? new Date(selectedItem.finishDate) : null,
                    startingPrice: selectedItem.startingPrice ?? null,
                    endingPrice: selectedItem.endingPrice ?? null,
                    currency: selectedItem.currency ?? 'TRY',
                    approvalGroupId: selectedItem.approvalGroupId ?? null
                });
            } else {
                // Yeni kayıt için formu resetle
                setFormData(defaultFormState);
            }
            setLocalSubmitted(false);
        }
    }, [visible, editMode, selectedItem, limitDetails]);

    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        setLocalSubmitted(true);

        const validationErrors = [];

        if (!formData.startDate) {
            validationErrors.push(th('validation.required'));
        }
        if (formData.startingPrice === null || formData.startingPrice === undefined) {
            validationErrors.push(th('validation.required'));
        }
        if (formData.endingPrice === null || formData.endingPrice === undefined) {
            validationErrors.push(th('validation.required'));
        }
        if (!formData.currency) {
            validationErrors.push(th('validation.required'));
        }
        if (!formData.approvalGroupId) {
            validationErrors.push(th('validation.required'));
        }

        if (formData.finishDate && formData.startDate) {
            const startDate = new Date(formData.startDate);
            const finishDate = new Date(formData.finishDate);

            if (finishDate <= startDate) {
                validationErrors.push(th('filter.dateRangeError'));
            }
        }

        if (validationErrors.length > 0) {
            validationErrors.forEach((error, index) => {
                setTimeout(() => {
                    toast.current?.show({
                        severity: 'error',
                        summary: th('common.error'),
                        detail: error,
                        life: 4000
                    });
                }, index * 200);
            });
            return;
        }

        if (onSubmit) {
            onSubmit(formData);
        } else {
            console.log('❌ onSubmit function is not provided');
        }
    };

    const handleClose = () => {
        onHide();
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={th('common.cancel')} icon="pi pi-times" outlined onClick={handleClose} />
            <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} />
        </div>
    );

    const approvalGroupOptions =
        approvalGroupsData?.items?.map((group) => ({
            label: group.groupName,
            value: group.id
        })) || [];

    return (
        <>
            <Toast ref={toast} />
            <Dialog visible={visible} style={{ width: '600px' }} header={editMode ? th('common.edit') : th('common.new')} modal className="p-fluid" footer={footer} onHide={handleClose}>
                <div className="p-fluid">
                    <div className="grid">
                        <div className="col-6">
                            <div className="field">
                                <label htmlFor="startDate" className="font-bold">
                                    {th('common.startDate')} <span className="text-red-500">*</span>
                                </label>
                                <Calendar
                                    id="startDate"
                                    value={formData.startDate}
                                    onChange={(e) => handleFieldChange('startDate', e.value)}
                                    showIcon
                                    dateFormat="dd/mm/yy"
                                    maxDate={formData.finishDate}
                                    className={localSubmitted && !formData.startDate ? 'p-invalid' : ''}
                                    placeholder={th('common.startDate')}
                                    disabled={isLoadingDetails}
                                />
                                {localSubmitted && !formData.startDate && <small className="p-error">{th('validation.required')}</small>}
                            </div>
                        </div>

                        <div className="col-6">
                            <div className="field">
                                <label htmlFor="finishDate" className="font-bold">
                                    {th('common.endDate')}
                                </label>
                                <Calendar
                                    id="finishDate"
                                    value={formData.finishDate}
                                    onChange={(e) => handleFieldChange('finishDate', e.value)}
                                    showIcon
                                    dateFormat="dd/mm/yy"
                                    minDate={formData.startDate}
                                    placeholder={th('common.endDate')}
                                    disabled={isLoadingDetails}
                                />
                            </div>
                        </div>

                        <div className="col-4">
                            <div className="field">
                                <label htmlFor="startingPrice" className="font-bold">
                                    {th('approvalLimits.startingPrice')} <span className="text-red-500">*</span>
                                </label>
                                <InputNumber
                                    id="startingPrice"
                                    value={formData.startingPrice}
                                    onValueChange={(e) => handleFieldChange('startingPrice', e.value)}
                                    mode="decimal"
                                    minFractionDigits={2}
                                    maxFractionDigits={2}
                                    className={localSubmitted && (formData.startingPrice === null || formData.startingPrice === undefined) ? 'p-invalid' : ''}
                                    placeholder={th('approvalLimits.startingPricePlaceholder')}
                                    disabled={isLoadingDetails}
                                />
                                {localSubmitted && (formData.startingPrice === null || formData.startingPrice === undefined) && <small className="p-error">{th('validation.required')}</small>}
                            </div>
                        </div>

                        <div className="col-4">
                            <div className="field">
                                <label htmlFor="endingPrice" className="font-bold">
                                    {th('approvalLimits.endingPrice')} <span className="text-red-500">*</span>
                                </label>
                                <InputNumber
                                    id="endingPrice"
                                    value={formData.endingPrice}
                                    onValueChange={(e) => handleFieldChange('endingPrice', e.value)}
                                    mode="decimal"
                                    minFractionDigits={2}
                                    maxFractionDigits={2}
                                    className={localSubmitted && (formData.endingPrice === null || formData.endingPrice === undefined) ? 'p-invalid' : ''}
                                    placeholder={th('approvalLimits.endingPricePlaceholder')}
                                    disabled={isLoadingDetails}
                                />
                                {localSubmitted && (formData.endingPrice === null || formData.endingPrice === undefined) && <small className="p-error">{th('validation.required')}</small>}
                            </div>
                        </div>

                        <div className="col-4">
                            <div className="field">
                                <label htmlFor="currency" className="font-bold">
                                    {th('labor.currency')} <span className="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    id="currency"
                                    value={formData.currency}
                                    options={currencyOptions}
                                    onChange={(e) => handleFieldChange('currency', e.value)}
                                    placeholder={th('labor.currency')}
                                    className={localSubmitted && !formData.currency ? 'p-invalid' : ''}
                                    disabled={isLoadingDetails}
                                />
                                {localSubmitted && !formData.currency && <small className="p-error">{th('validation.required')}</small>}
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="field">
                                <label htmlFor="approvalGroupId" className="font-bold">
                                    {th('approvalGroups.title')} <span className="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    id="approvalGroupId"
                                    value={formData.approvalGroupId}
                                    options={approvalGroupOptions}
                                    onChange={(e) => handleFieldChange('approvalGroupId', e.value)}
                                    placeholder={th('approvalGroups.title')}
                                    className={localSubmitted && !formData.approvalGroupId ? 'p-invalid' : ''}
                                    disabled={isLoadingDetails}
                                />
                                {localSubmitted && !formData.approvalGroupId && <small className="p-error">{th('validation.required')}</small>}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default ApprovalLimitsForm;
